import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Upload, X, Check, ImageIcon, UserCircle2, Crop } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";
import PhotoCropperDialog from "@/components/PhotoCropperDialog";

// Normalize Arabic + latin names for matching:
// - lowercase, remove diacritics, unify alef/yaa/taa marbuta forms, strip non-alnum
function normalizeName(s) {
  if (!s) return "";
  return String(s)
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u064B-\u0652\u0670]/g, "") // arabic diacritics
    .replace(/[إأآا]/g, "ا")
    .replace(/ى/g, "ي")
    .replace(/ة/g, "ه")
    .replace(/[^\p{L}\p{N}]/gu, "");
}

function tokens(s) {
  return normalizeName(s).length ? [normalizeName(s)] : [];
}

// Score how well a file name matches a teacher name.
// Higher = better. Considers full contains + token overlap.
function scoreMatch(fileBase, teacherName) {
  const f = normalizeName(fileBase);
  const t = normalizeName(teacherName);
  if (!f || !t) return 0;
  if (f === t) return 1000;
  if (f.includes(t)) return 500 + t.length;
  if (t.includes(f)) return 400 + f.length;
  // token overlap
  const ft = (fileBase || "").split(/[\s._\-]+/).map(normalizeName).filter(Boolean);
  const tt = (teacherName || "").split(/\s+/).map(normalizeName).filter(Boolean);
  let overlap = 0;
  for (const a of ft) {
    for (const b of tt) {
      if (!a || !b) continue;
      if (a === b) overlap += 20;
      else if (a.length >= 3 && (a.includes(b) || b.includes(a))) overlap += 8;
    }
  }
  return overlap;
}

function fileBaseName(f) {
  const n = f.name || "";
  return n.replace(/\.[^.]+$/, "");
}

async function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function BulkPhotoUploadDialog({ open, onClose, teachers, onDone }) {
  const [items, setItems] = useState([]); // {file, previewUrl, assignedTeacherId, autoMatched, score, croppedDataUrl}
  const [uploading, setUploading] = useState(false);
  const [cropTarget, setCropTarget] = useState(null); // {index, sourceUrl}

  useEffect(() => {
    if (!open) {
      setItems([]);
    }
  }, [open]);

  const usedTeacherIds = useMemo(() => new Set(items.map((it) => it.assignedTeacherId).filter(Boolean)), [items]);

  const autoMatch = (file) => {
    const base = fileBaseName(file);
    let best = { id: "", score: 0 };
    for (const t of teachers) {
      const s = scoreMatch(base, t.name);
      if (s > best.score) best = { id: t.id, score: s };
    }
    return best;
  };

  const addFiles = async (files) => {
    const imgs = Array.from(files || []).filter((f) => f.type.startsWith("image/"));
    if (imgs.length === 0) { toast.error("لم يتم اختيار صور صالحة"); return; }
    const enriched = await Promise.all(imgs.map(async (f) => {
      const match = autoMatch(f);
      const previewUrl = URL.createObjectURL(f);
      return {
        file: f,
        previewUrl,
        assignedTeacherId: match.id,
        autoMatched: !!match.id && match.score >= 20,
        score: match.score,
      };
    }));
    setItems((prev) => [...prev, ...enriched]);
  };

  const onDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  };

  const removeItem = (idx) => {
    setItems((prev) => {
      const copy = [...prev];
      try { URL.revokeObjectURL(copy[idx]?.previewUrl); } catch { /* noop */ }
      copy.splice(idx, 1);
      return copy;
    });
  };

  const updateAssign = (idx, teacherId) => {
    setItems((prev) => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], assignedTeacherId: teacherId, autoMatched: false };
      return copy;
    });
  };

  const canConfirm = items.length > 0 && items.every((it) => !!it.assignedTeacherId) && !uploading;
  const matchedCount = items.filter((it) => !!it.assignedTeacherId).length;

  const confirm = async () => {
    // Check for duplicate teacher assignments
    const seen = new Map();
    for (const it of items) {
      if (seen.has(it.assignedTeacherId)) {
        toast.error("لا يمكن إسناد أكثر من صورة لنفس المعلم — راجع القائمة");
        return;
      }
      seen.set(it.assignedTeacherId, true);
    }
    setUploading(true);
    let ok = 0, fail = 0;
    for (const it of items) {
      const teacher = teachers.find((t) => t.id === it.assignedTeacherId);
      if (!teacher) { fail++; continue; }
      if (!it.croppedDataUrl && it.file.size > 1_400_000) { fail++; toast.error(`${teacher.name}: الصورة أكبر من 1MB — قصّها أولاً`); continue; }
      try {
        const dataUrl = it.croppedDataUrl || (await fileToDataUrl(it.file));
        await api.patch(`/users/${teacher.id}/photo`, { photo_url: dataUrl });
        ok++;
      } catch (e) { fail++; toast.error(`${teacher.name}: ${extractError(e)}`); }
    }
    setUploading(false);
    toast.success(`تم رفع ${ok} صورة${fail ? ` — فشل: ${fail}` : ""}`);
    if (onDone) await onDone();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !uploading) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="bulk-photo-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-black flex items-center gap-2">
            <ImageIcon className="w-6 h-6 text-indigo-600" /> رفع صور متعددة للمعلمين
          </DialogTitle>
        </DialogHeader>

        <div className="text-xs text-slate-500 mb-3">
          اسحب صور المعلمين هنا — سيتم مطابقتها تلقائياً بأسماء الملفات مع أسماء المعلمين.
          يمكنك تصحيح أي مطابقة يدوياً قبل التأكيد.
        </div>

        <div
          onDrop={onDrop}
          onDragOver={(e) => e.preventDefault()}
          className="rounded-2xl border-2 border-dashed border-indigo-300 p-8 text-center bg-indigo-50/50 mb-4"
          data-testid="bulk-drop-zone"
        >
          <Upload className="w-10 h-10 mx-auto text-indigo-500 mb-2" />
          <div className="font-black text-slate-700">اسحب الصور هنا أو</div>
          <label className="inline-flex mt-3">
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
              className="hidden"
              data-testid="bulk-file-input"
            />
            <span className="cursor-pointer px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700">
              اختر عدة صور
            </span>
          </label>
          <div className="text-[11px] text-slate-500 mt-3">
            سمِّ الملفات مثل: <span className="font-mono" dir="ltr">ahmed_ali.jpg</span> أو <span dir="rtl">أحمد_علي.jpg</span> للمطابقة التلقائية.
          </div>
        </div>

        {items.length > 0 && (
          <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
            <div className="px-2 py-1 rounded-full bg-slate-100 font-bold">
              الإجمالي: {items.length}
            </div>
            <div className="px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 font-bold">
              مطابقة: {matchedCount}
            </div>
            <div className="px-2 py-1 rounded-full bg-amber-100 text-amber-700 font-bold">
              يحتاج مراجعة: {items.length - matchedCount}
            </div>
          </div>
        )}

        <div className="space-y-3">
          {items.map((it, i) => {
            const teacher = teachers.find((t) => t.id === it.assignedTeacherId);
            const dup = it.assignedTeacherId && items.filter((x) => x.assignedTeacherId === it.assignedTeacherId).length > 1;
            return (
              <div key={i} className={`flex items-center gap-3 p-3 rounded-2xl border-2 ${dup ? "border-rose-300 bg-rose-50" : it.autoMatched ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200 bg-white"}`} data-testid={`bulk-item-${i}`}>
                <div className="relative shrink-0">
                  <img src={it.croppedDataUrl || it.previewUrl} alt="" className={`w-16 h-16 object-cover border ${it.croppedDataUrl ? "rounded-full ring-2 ring-indigo-400" : "rounded-xl"}`} />
                  {it.croppedDataUrl && (
                    <span className="absolute -top-1 -right-1 bg-indigo-600 text-white rounded-full w-4 h-4 flex items-center justify-center" title="تم القص"><Check className="w-2.5 h-2.5" /></span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-slate-500 truncate" dir="ltr">{it.file.name}</div>
                  <div className="mt-1 flex items-center gap-2 flex-wrap">
                    {it.autoMatched && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-black">
                        <Check className="w-3 h-3" /> مطابقة تلقائية
                      </span>
                    )}
                    {dup && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 text-[10px] font-black">
                        تكرار — اختر معلمًا آخر
                      </span>
                    )}
                    <div className="min-w-[200px]">
                      <Select value={it.assignedTeacherId || ""} onValueChange={(v) => updateAssign(i, v)}>
                        <SelectTrigger className="h-9 text-xs" data-testid={`bulk-assign-${i}`}>
                          <SelectValue placeholder="اختر المعلم" />
                        </SelectTrigger>
                        <SelectContent>
                          {teachers.map((t) => (
                            <SelectItem key={t.id} value={t.id}>
                              {t.name} {usedTeacherIds.has(t.id) && t.id !== it.assignedTeacherId ? " (مسند)" : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {teacher?.photo_url && (
                      <span className="text-[10px] text-amber-700 font-bold">يوجد صورة سابقة — سيتم استبدالها</span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-1.5">
                  <Button size="sm" variant="outline" onClick={() => setCropTarget({ index: i, sourceUrl: it.previewUrl })} className="gap-1 text-indigo-700 border-indigo-200" data-testid={`bulk-crop-${i}`}>
                    <Crop className="w-3.5 h-3.5" /> {it.croppedDataUrl ? "إعادة القص" : "قص مربّع"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => removeItem(i)} className="gap-1 text-rose-600 border-rose-200" data-testid={`bulk-remove-${i}`}>
                    <X className="w-3.5 h-3.5" /> إزالة
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
          <Button variant="outline" onClick={onClose} disabled={uploading} data-testid="bulk-cancel-btn">إلغاء</Button>
          <Button
            onClick={confirm}
            disabled={!canConfirm}
            className="gap-2 bg-gradient-to-l from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700"
            data-testid="bulk-confirm-btn"
          >
            <UserCircle2 className="w-4 h-4" /> {uploading ? "جاري الرفع..." : `رفع ${items.length} صورة`}
          </Button>
        </div>

        <PhotoCropperDialog
          open={!!cropTarget}
          onClose={() => setCropTarget(null)}
          imageUrl={cropTarget?.sourceUrl}
          onCropped={(dataUrl) => {
            const idx = cropTarget?.index;
            if (idx == null) return;
            setItems((prev) => {
              const copy = [...prev];
              copy[idx] = { ...copy[idx], croppedDataUrl: dataUrl };
              return copy;
            });
            toast.success("تم قص الصورة");
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
